import React from "react"

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  CompletionIndicator,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Tooltip,
} from "@coval/ui"
import {
  AlertTriangle,
  Info,
  MessageSquareText,
  Pencil,
  RefreshCcw,
  VolumeX,
} from "lucide-react"

import { getAvatarColor } from "@/utils/avatarIdentity"

import type { PersonaFormController } from "../hooks/usePersonaFormController"
import {
  EYES_VARIANTS,
  HAIR_VARIANTS,
  LIPS_VARIANTS,
} from "../utils/personaModalUtils"
import { PersonaAdvancedFields } from "./PersonaAdvancedFields"
import { PersonaCallEnvironmentCard } from "./PersonaCallEnvironmentCard"
import { PersonaVoiceCard } from "./PersonaVoiceCard"

interface PersonaFormFieldsProps {
  controller: PersonaFormController
  embedded?: boolean
  nameError?: string
  showCharacteristicsError: boolean
}

const PROMPT_PLACEHOLDER =
  'You are a customer who is in a hurry. You speak quickly and expect fast answers. You tend to go off topic and ramble about unrelated things. You say "like" and "um" frequently. You get frustrated with lengthy explanations and may interrupt the agent mid-sentence.'

const FACE_VARIANT_TABS = [
  { value: "hair", label: "Hair", variants: HAIR_VARIANTS },
  { value: "eyes", label: "Eyes", variants: EYES_VARIANTS },
  { value: "lips", label: "Lips", variants: LIPS_VARIANTS },
] as const

/**
 * Presentational persona form body shared by the editable form (`PersonaForm`)
 * and the read-only version-history preview. All state lives in the controller;
 * read-only behavior is applied by the caller wrapping this in a disabled
 * `<fieldset>`, mirroring the metric version-history view.
 */
export const PersonaFormFields: React.FC<PersonaFormFieldsProps> = ({
  controller,
  embedded = false,
  nameError,
  showCharacteristicsError,
}) => {
  const {
    selectedPersona,
    isBuiltIn,
    isSpeechDisabled,
    isLegacy,
    formData,
    handleChange,
    isPromptExpanded,
    setIsPromptExpanded,
    isInSilentMode,
    randomColorIndex,
    isFaceSelectorOpen,
    setIsFaceSelectorOpen,
    hairPreviews,
    eyesPreviews,
    lipsPreviews,
    handleFaceCustomization,
    handleRegenerateSeed,
  } = controller

  const promptHeadingId = React.useId()
  const hasName = formData.display_name.trim().length > 0
  const hasPrompt = (formData.simulated_user_prompt ?? "").trim().length > 0

  const facePreviews = {
    hair: hairPreviews,
    eyes: eyesPreviews,
    lips: lipsPreviews,
  }

  return (
    <div
      className={cn(
        "space-y-3",
        embedded &&
          "divide-y divide-coval-border space-y-0 [&>div]:rounded-none [&>div]:border-0",
      )}
    >
      {isInSilentMode && (
        <Alert
          variant="info"
          icon={<VolumeX />}
          className="border border-coval-border bg-surface-0 text-coval-text"
        >
          <AlertTitle>Silent Mode is on</AlertTitle>
          <AlertDescription>
            This persona never speaks, so its prompt, voice, and call
            environment are not used. Turn Silent Mode off under Advanced to
            edit them.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-5">
            <div className="relative shrink-0">
              <div
                className="flex h-20 w-20 items-end justify-center overflow-hidden rounded-full"
                style={{
                  backgroundColor: getAvatarColor(
                    selectedPersona?.id || "",
                    randomColorIndex,
                  ),
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element -- avatar is a generated remote SVG, not an optimizable asset */}
                <img
                  src={formData.avatar_url}
                  className="h-24 w-24 rounded-full pt-3"
                  alt="Persona avatar"
                />
              </div>
              <Button
                type="button"
                icon
                variant="secondary"
                tooltip="Edit persona avatar"
                className="absolute -bottom-1 -right-1 z-10 rounded-full"
                onClick={() => setIsFaceSelectorOpen(true)}
                disabled={isBuiltIn}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>

            <FormField
              label="Persona Name"
              className="flex-1 pt-1"
              error={nameError}
            >
              {(ids) => (
                <Input
                  {...ids}
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleChange}
                  disabled={isBuiltIn}
                  error={Boolean(nameError)}
                  data-testid="persona-name-input"
                />
              )}
            </FormField>
            {/* Sits on the label's line, where every other section carries its
                indicator, rather than beside the input below it. */}
            <CompletionIndicator
              complete={hasName}
              label="Persona name"
              className="mt-1.5"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="w-full" data-covi-highlight-field="persona_prompt">
        <CardHeader className="p-6 pb-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <CardTitle
                id={promptHeadingId}
                className="flex items-center gap-2"
              >
                <MessageSquareText className="size-4 shrink-0" />
                Prompt
              </CardTitle>
              <Tooltip
                content={
                  <p className="max-w-xs">
                    Personality, tone, and speech patterns the persona acts out.
                    Does not control voice or pronunciation.
                  </p>
                }
              >
                <button type="button" aria-label="About the prompt">
                  <Info className="size-3.5 text-coval-text-muted" />
                </button>
              </Tooltip>
            </div>
            {/* Silent Mode doesn't use the prompt, and validation doesn't
                require it there, so the step isn't outstanding. */}
            {!isInSilentMode && (
              <CompletionIndicator complete={hasPrompt} label="Prompt" />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3 px-6 pb-6 pt-0">
          <Textarea
            aria-labelledby={promptHeadingId}
            aria-invalid={showCharacteristicsError || undefined}
            name="simulated_user_prompt"
            rows={8}
            className="resize-none"
            placeholder={PROMPT_PLACEHOLDER}
            value={formData.simulated_user_prompt}
            onChange={handleChange}
            error={showCharacteristicsError}
            disabled={isSpeechDisabled}
            data-testid="persona-prompt-textarea"
          />
          {showCharacteristicsError && (
            <p className="text-xs text-coval-danger-text">
              Please fill out this field.
            </p>
          )}
        </CardContent>
      </Card>

      {isLegacy && (
        <Alert variant="warning" icon={<AlertTriangle />}>
          <strong>Voice update required:</strong> select a voice and language to
          update this persona.
        </Alert>
      )}

      <PersonaVoiceCard controller={controller} />

      <PersonaCallEnvironmentCard controller={controller} />

      <PersonaAdvancedFields controller={controller} />

      <Dialog open={isFaceSelectorOpen} onOpenChange={setIsFaceSelectorOpen}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Customize Face</DialogTitle>
          </DialogHeader>
          {/* Body never scrolls; only the variant grid does, so the preview,
              tabs, and footer stay put while browsing. */}
          <DialogBody className="overflow-hidden">
            <div className="grid h-[240px] grid-cols-2 gap-6">
              <div className="relative flex h-full flex-col items-center justify-center">
                <div className="relative h-32 w-32">
                  <div
                    className="flex h-32 w-32 items-end justify-center overflow-hidden rounded-full"
                    style={{
                      backgroundColor: getAvatarColor(
                        selectedPersona?.id || "",
                        randomColorIndex,
                      ),
                    }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element -- avatar is a generated remote SVG, not an optimizable asset */}
                    <img
                      src={formData.avatar_url}
                      className="h-40 w-40 rounded-full pt-5"
                      alt="Preview"
                    />
                  </div>
                  <Button
                    type="button"
                    icon
                    variant="secondary"
                    tooltip="Regenerate avatar"
                    className="absolute -bottom-1 -right-1 z-10 rounded-full"
                    onClick={() => {
                      handleRegenerateSeed()
                    }}
                  >
                    <RefreshCcw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Tabs
                defaultValue="hair"
                className="flex h-full min-h-0 flex-col"
              >
                <TabsList className="shrink-0">
                  {FACE_VARIANT_TABS.map((tab) => (
                    <TabsTrigger key={tab.value} value={tab.value}>
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {FACE_VARIANT_TABS.map((tab) => (
                  <TabsContent
                    key={tab.value}
                    value={tab.value}
                    className="min-h-0 flex-1 overflow-y-auto pr-1"
                  >
                    <div className="grid grid-cols-6 gap-2">
                      {tab.variants.map((variant, idx) => (
                        <button
                          key={variant}
                          className={`rounded-full border-2 ${formData.face_customization?.[tab.value] === variant ? "border-coval-border-active" : "border-transparent"}`}
                          onClick={() => {
                            handleFaceCustomization(tab.value, variant)
                          }}
                          type="button"
                        >
                          {/* eslint-disable-next-line @next/next/no-img-element -- avatar is a generated remote SVG, not an optimizable asset */}
                          <img
                            src={`${facePreviews[tab.value][idx]}&scale=150&translateY=25`}
                            className="h-9 w-9 rounded-full object-cover"
                            alt={variant}
                          />
                        </button>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsFaceSelectorOpen(false)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isPromptExpanded} onOpenChange={setIsPromptExpanded}>
        <DialogContent className="w-full max-w-4xl max-h-[95vh] min-h-[800px] h-full flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle>Edit Prompt</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex-1 min-h-0 p-6">
            <Textarea
              aria-label="Prompt"
              name="simulated_user_prompt"
              placeholder={PROMPT_PLACEHOLDER}
              value={formData.simulated_user_prompt}
              onChange={handleChange}
              disabled={isBuiltIn}
              className="h-full min-h-full resize-none"
            />
          </DialogBody>
          <DialogFooter className="shrink-0">
            <Button type="button" onClick={() => setIsPromptExpanded(false)}>
              Done (esc)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
